import { Check, Minus } from '../../icons';
import { Platform } from '../../platform';
import { Pressable, type PressEvent } from '../../primitives/Pressable';
import { View } from '../../primitives/View';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { checkboxAccessibilityRole } from './Checkbox.a11y';
import { CHECK_SIZE_FOR, CHECK_STROKE, resolveCheckColor } from './Checkbox.icons';
import type { CheckboxProps } from './Checkbox.types';
import { checkboxVariants } from './Checkbox.variants';

// A button on iOS, where `checkbox` carries no trait — see `Checkbox.a11y.ts`.
const CHECKBOX_ROLE = checkboxAccessibilityRole(Platform.OS);

/**
 * Cross-platform Checkbox. Renders identically on web and React Native — a
 * bordered box with a centered `Check` glyph shown when `checked`. Mirrors the
 * Figma "Flashka Design System" node `3418:2713`. See `Checkbox.variants.ts`
 * for the per-state spec + anatomy.
 *
 * Controlled-only — the caller owns the boolean:
 *
 * @example
 * ```tsx
 * import { Checkbox } from '@flashka/shared/ui';
 *
 * const [agreed, setAgreed] = useState(false);
 * <Checkbox checked={agreed} onCheckedChange={setAgreed} accessibilityLabel="Accept terms" />
 *
 * // Brand-filled + large
 * <Checkbox variant="primary" size="l" checked onCheckedChange={() => {}} />
 * ```
 */
export function Checkbox({
  checked = false,
  indeterminate = false,
  onCheckedChange,
  size = 'm',
  variant = 'default',
  disabled = false,
  interactive = true,
  haptic = 'selection',
  checkColor,
  className,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
  onHoverIn,
  onHoverOut,
}: CheckboxProps) {
  const { isDark } = useTheme();

  // CVA's VariantProps keep `null` in the union even with a default value, so
  // coalesce before indexing the icon size / color maps (which are non-null).
  const resolvedSize = size ?? 'm';
  const resolvedVariant = variant ?? 'default';

  // Indeterminate ("mixed") renders a dash and fills the box like a selected
  // one; it wins over `checked` for the glyph.
  const glyphColor = checkColor ?? resolveCheckColor(resolvedVariant, isDark);
  const glyphSize = CHECK_SIZE_FOR[resolvedSize];
  const glyph = indeterminate ? (
    <Minus color={glyphColor} size={glyphSize} strokeWidth={CHECK_STROKE} />
  ) : checked ? (
    <Check color={glyphColor} size={glyphSize} strokeWidth={CHECK_STROKE} />
  ) : null;

  const filled = checked || indeterminate;
  const boxClass = cn(checkboxVariants({ size, variant, selected: filled }), className);

  // Presentational mode — a pure visual indicator (no role, focus, press, or
  // haptic) for embedding inside a larger clickable card/row that owns the
  // checkbox semantics. `pointer-events-none` so it never intercepts the
  // parent's press or reacts to hover; hidden from assistive tech.
  if (!interactive) {
    return (
      <View
        className={cn(boxClass, 'web:pointer-events-none')}
        testID={testID}
        webProps={{ 'aria-hidden': true, ...(webProps ?? {}) }}
        nativeProps={{ accessible: false, ...(nativeProps ?? {}) }}>
        {glyph}
      </View>
    );
  }

  const handlePress = (event: PressEvent) => {
    if (!disabled) onCheckedChange?.(!checked, event);
  };

  // Announce checkbox + state to assistive tech on both platforms. `role`
  // comes from `accessibilityRole`; `aria-checked` (web) / `accessibilityState`
  // (native) carry the value. Caller props win on key overlap.
  const ariaChecked: boolean | 'mixed' = indeterminate ? 'mixed' : checked;
  const mergedWebProps = { 'aria-checked': ariaChecked, ...(webProps ?? {}) };
  const mergedNativeProps = {
    accessibilityState: { checked: ariaChecked, disabled },
    ...(nativeProps ?? {}),
  };

  return (
    <Pressable
      className={boxClass}
      disabled={disabled}
      haptic={haptic}
      onPress={handlePress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      accessibilityRole={CHECKBOX_ROLE}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      webProps={mergedWebProps}
      nativeProps={mergedNativeProps}>
      {/* The Pressable carries the checkbox role + label; the glyph is decorative. */}
      {glyph}
    </Pressable>
  );
}
(Checkbox as { displayName?: string }).displayName = 'Checkbox';
